import { useCallback, useEffect, useRef, useState } from 'react';
import { useFirestoreDocData } from 'reactfire';
import styled from 'styled-components';

import { useDebounce, useIsQuestionnaireDisabled } from '@aphrodite/common/hooks';
import { useAuthHelper } from '@aphrodite/common/hooks';
import { IUserProfile } from '@aphrodite/common/types/firestore-types';
import { firebaseFirestore } from '@aphrodite/firebase/firebase';
import { DocumentReference, doc, updateDoc } from '@firebase/firestore';

import { PROFILE_INTEREST_REF } from '../../constants/Questionnaire';
import { IQuestionAnswer } from '../../constants/Questionnaire';
import DashboardLoading from '../dashboard/DashboardLoading';
import { IStatus } from '../questionnaire/Questionnaire';
import ProfileInfoCard from './ProfileInfoCard';

// Styled Components
const StyledTextArea = styled.textarea<{ textAreaHeight: string }>`
  background-color: ${(props) => (!props.disabled ? props.theme.colors.paleGrey : 'transparent')};
  border: none;
  border-radius: 16px;
  height: ${(props) => props.textAreaHeight};
  max-height: 200px;
  margin-top: 12px;
  margin-left: -8px;
  outline: none;
  padding: 8px 12px;
  resize: none;
  width: 100%;
  vertical-align: top;
`;
export default function ProfileInterestCard(): React.ReactElement {
  const { user } = useAuthHelper();
  const userProfileRef = doc(
    firebaseFirestore,
    `UserProfiles/${user!.uid}`,
  ) as DocumentReference<IUserProfile>;
  const { status: userProfileStatus, data: userProfileData } =
    useFirestoreDocData<IUserProfile>(userProfileRef);
  const interest = userProfileData[PROFILE_INTEREST_REF];
  const [editingEnabled, setEditingEnabled] = useState(false);
  const [response, setResponse] = useState<IQuestionAnswer>(interest ?? '');
  const [status, setStatus] = useState<IStatus>('saved');

  let textAreaRef: any = useRef<HTMLDivElement>(null);
  const [textAreaHeight, setTextAreaHeight] = useState('auto');
  useEffect(() => {
    setTextAreaHeight(`${textAreaRef.current!.scrollHeight}px`);
  }, [response]);

  const isQuestionnaireDisabled = useIsQuestionnaireDisabled();
  const debouncedResponse = useDebounce(response, 500);
  const isValidResponse = (interest: string) => {
    return interest && interest.replace(/\s/g, '').length !== 0;
  };
  const onStatusChange = useCallback((status: IStatus) => setStatus(status), []);
  function onToggleEditMode() {
    setEditingEnabled(!editingEnabled);
  }
  function onClickSave() {
    onStatusChange('loading');
    if (
      !isValidResponse(debouncedResponse as string) ||
      debouncedResponse === null ||
      debouncedResponse === undefined
    ) {
      return;
    }
    updateDoc(userProfileRef, {
      [PROFILE_INTEREST_REF]: debouncedResponse as string,
    })
      .then(() => {
        onStatusChange('saved');
      })
      .catch((error: any) => {
        onStatusChange('error');
      });
    setEditingEnabled(false);
  }
  function handleInputChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setResponse(event.target.value);
    setTextAreaHeight('auto');
  }

  if (userProfileStatus === 'loading') {
    return <DashboardLoading />;
  }
  return (
    <form>
      <ProfileInfoCard
        isEditMode={editingEnabled}
        onSave={onClickSave}
        onToggleEditMode={onToggleEditMode}
        status={status}
        title="My Interests"
        icon="interest"
        isDisabled={isQuestionnaireDisabled}
      >
        <StyledTextArea
          disabled={!editingEnabled}
          onChange={(e) => handleInputChange(e)}
          value={response as string}
          maxLength={200}
          textAreaHeight={textAreaHeight}
          ref={textAreaRef}
        />
      </ProfileInfoCard>
    </form>
  );
}
